import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import WorkflowGraph from "@/view/workflow/components/workflowGraph/WorkflowGraph";
import type {AddEdgeBranchPayload, UpdateBranchEdgePayload} from "@/view/workflow/components/workflowGraph/LayoutFlow";
import {useParams} from "react-router-dom";
import useWorkflow from "@/query/workflow/useWorkflow";
import {InputOperator, Operator, OperatorTypeEnum, Workflow} from "@/model/Workflow";
import {useRecoilState} from "recoil";
import editingOperatorIdState from "@/view/workflow/editingOperatorContext";
import OperatorForm from "@/view/workflow/components/operatorForm/OperatorForm";
import {ChevronsRight} from "lucide-react";
import OperatorIcon from "@/view/workflow/components/OperatorIcon";
import {removeOperator} from "@/view/workflow/helper/removeOperatorHelper";
import {generateDefaultOperator} from "@/view/workflow/helper/generateOperatorHelper";
import toast from "react-hot-toast";
import type {OperatorNode} from "@/view/workflow/components/workflowGraph/types";
import {SiteBunnyAdminError} from "@/error";
import WorkflowSaveDialog, {SAVE_WORKFLOW_MODAL_ID} from "@/view/workflow/components/WorkflowSaveDialog";
import useModal from "@/hook/useModal";
import {Button} from "@/components/ui/button";

const cloneWorkflow = (workflow: Workflow): Workflow => JSON.parse(JSON.stringify(workflow));

const WorkflowGraphView: React.FC = () => {
    const {workflowId} = useParams<{ workflowId: string }>();
    const modal = useModal();

    const [renderableWorkflow, setRenderableWorkflow] = useState<Workflow | undefined>(undefined);
    const editableWorkflowRef = useRef<Workflow | undefined>(undefined);

    const {data: workflow} = useWorkflow(workflowId!)
    const [editingOperatorId, setEditingOperatorId] = useRecoilState(editingOperatorIdState);

    const editableWorkflow = editableWorkflowRef.current;
    const editingOperator: Operator | InputOperator | undefined = (() => {
        if (!editableWorkflow || !editingOperatorId)
            return undefined;

        if (editingOperatorId === 'input')
            return {
                _id: "input",
                type: "input",
                name: "input",
                parameter: editableWorkflow.defaultParameter ?? {},
                nextOperatorId: editableWorkflow.firstOperatorId
            };

        return editableWorkflow.operators[editingOperatorId];
    })();

    const operators = useMemo(() => {
        if (!renderableWorkflow)
            return undefined;

        const result = JSON.parse(JSON.stringify(renderableWorkflow.operators));
        result['input'] = {
            _id: "input",
            type: "input",
            name: "input",
            parameter: renderableWorkflow.defaultParameter,
            nextOperatorId: renderableWorkflow.firstOperatorId
        } as InputOperator;
        return result;
    }, [renderableWorkflow])

    const syncRenderableWorkflow = useCallback(() => {
        const editableWorkflow = editableWorkflowRef.current;
        if (!editableWorkflow)
            return;

        setRenderableWorkflow(cloneWorkflow(editableWorkflow));
    }, []);

    const applyEditableWorkflow = useCallback((updater: (workflow: Workflow) => void) => {
        const editableWorkflow = editableWorkflowRef.current;
        if (!editableWorkflow)
            return;

        updater(editableWorkflow);
        setRenderableWorkflow(cloneWorkflow(editableWorkflow));
    }, []);

    const addOperator = useCallback((node: OperatorNode) => {
        if (!node)
            return;
        if (node.data.type === "input")
            return;

        const operatorType = node.data.type as OperatorTypeEnum;
        applyEditableWorkflow((editableWorkflow) => {
            try {
                const operator = generateDefaultOperator(node.id, operatorType, node.data.name);
                editableWorkflow.operators = {
                    ...editableWorkflow.operators,
                    [operator._id]: operator
                }
            } catch (e) {
                if (e instanceof SiteBunnyAdminError)
                    toast.error(e.message);
            }
        });
    }, [applyEditableWorkflow]);

    const deleteOperator = useCallback((operatorId: string) => {
        applyEditableWorkflow((editableWorkflow) => {
            const result = removeOperator(editableWorkflow, operatorId);
            editableWorkflow.operators = result.operators;
        });
    }, [applyEditableWorkflow]);

    const addEdge = useCallback((
        source: string, target: string, branch?: AddEdgeBranchPayload
    ) => {
        applyEditableWorkflow((editableWorkflow) => {
            if (source === "input") {
                editableWorkflow.firstOperatorId = target;
                return;
            }

            const sourceOp = editableWorkflow.operators[source];
            if (!sourceOp)
                return;

            if (sourceOp.type === OperatorTypeEnum.BRANCH_PYTHON) {
                if (!branch?.branchKey)
                    return;

                editableWorkflow.operators[source] = {
                    ...sourceOp,
                    branchMap: {...sourceOp.branchMap, [branch.branchKey]: target},
                };
                return;
            }

            if (sourceOp.type === OperatorTypeEnum.FAN_OUT) {
                if (sourceOp.nextOperatorIds.includes(target))
                    return;

                editableWorkflow.operators[source] = {
                    ...sourceOp,
                    nextOperatorIds: [...sourceOp.nextOperatorIds, target],
                };
                return;
            }

            editableWorkflow.operators[source] = {
                ...sourceOp,
                nextOperatorId: target
            };
        });
    }, [applyEditableWorkflow]);

    const updateBranchEdge = useCallback((
        source: string, target: string, payload: UpdateBranchEdgePayload
    ) => {
        applyEditableWorkflow((editableWorkflow) => {
            const sourceOp = editableWorkflow.operators[source];
            if (!sourceOp || sourceOp.type !== OperatorTypeEnum.BRANCH_PYTHON)
                return;

            const { previousBranchKey, branchKey } = payload;
            const isExistingKey = previousBranchKey in sourceOp.branchMap;
            const newBranchMap = isExistingKey
                ? Object.fromEntries(
                    Object.entries(sourceOp.branchMap).map(([key, value]) =>
                        key === previousBranchKey ? [branchKey, target] : [key, value]
                    )
                )
                : { ...sourceOp.branchMap, [branchKey]: target };

            editableWorkflow.operators[source] = {
                ...sourceOp,
                branchMap: newBranchMap
            };
        });
    }, [applyEditableWorkflow]);

    const deleteEdge = useCallback((
        source: string, target: string, branch?: AddEdgeBranchPayload
    ) => {
        applyEditableWorkflow((editableWorkflow) => {
            if (source === "input") {
                if (editableWorkflow.firstOperatorId !== target)
                    return;

                editableWorkflow.firstOperatorId = "";
                return;
            }

            const sourceOp = editableWorkflow.operators[source];
            if (!sourceOp)
                return;

            if (sourceOp.type === OperatorTypeEnum.BRANCH_PYTHON) {
                if (!branch || sourceOp.branchMap[branch.branchKey] !== target)
                    return;

                const {[branch.branchKey]: _removed, ...nextBranchMap} = sourceOp.branchMap;
                editableWorkflow.operators[source] = {
                    ...sourceOp,
                    branchMap: nextBranchMap,
                };
                return;
            }

            if (sourceOp.type === OperatorTypeEnum.FAN_OUT) {
                if (!sourceOp.nextOperatorIds.includes(target))
                    return;

                editableWorkflow.operators[source] = {
                    ...sourceOp,
                    nextOperatorIds: sourceOp.nextOperatorIds.filter((operatorId) => operatorId !== target),
                };
                return;
            }

            if (sourceOp.nextOperatorId === target) {
                editableWorkflow.operators[source] = {
                    ...sourceOp,
                    nextOperatorId: undefined,
                };
            }
        });
    }, [applyEditableWorkflow]);

    const onClickCloseEditingPanel = (e: React.MouseEvent) => {
        e.stopPropagation();
        setEditingOperatorId(undefined);
    }

    const onClickSaveWorkflow = () => {
        modal.open(SAVE_WORKFLOW_MODAL_ID);
    }

    useEffect(() => {
        if (!workflow) {
            editableWorkflowRef.current = undefined;
            setRenderableWorkflow(undefined);
            return;
        }

        const editableWorkflow = cloneWorkflow(workflow);
        editableWorkflowRef.current = editableWorkflow;
        setRenderableWorkflow(cloneWorkflow(editableWorkflow));
    }, [workflow]);

    useEffect(() => {
        return () => setEditingOperatorId(undefined);
    }, [setEditingOperatorId]);

    if (!renderableWorkflow || !operators)
        return null;

    return (
        <div className='h-full flex flex-col'>
            {(editableWorkflow && editingOperator) && (
                <div className='rounded-md fixed z-10 gap-1 bg-background shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500 h-[100%] right-0 bottom-0 border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right w-1/2 flex'>
                    <div className='grow-0 shrink-0 flex items-center hover:bg-gray-100 px-1'
                         role="button"
                         onClick={onClickCloseEditingPanel}
                    >
                        <ChevronsRight/>
                    </div>
                    <div className='h-full overflow-y-auto flex flex-col w-full py-6'>
                        <div className='mb-3 flex items-center space-x-3'>
                            <OperatorIcon type={editingOperator.type}/>
                            <h2 className='font-semibold'>{editingOperator.type}</h2>
                        </div>
                        <OperatorForm workflow={editableWorkflow}
                                      operator={editingOperator}
                                      syncWorkflow={syncRenderableWorkflow}
                        />
                    </div>
                </div>
            )}
            <WorkflowGraph operators={operators}
                           editable={true}
                           onAddOperator={addOperator}
                           onDeleteOperator={deleteOperator}
                           onAddEdge={addEdge}
                           onUpdateBranchEdge={updateBranchEdge}
                           onDeleteEdge={deleteEdge}
                           toolbarActions={
                               <Button size={'sm'} onClick={onClickSaveWorkflow}>Deploy</Button>
                           }
            />

            {editableWorkflow && <WorkflowSaveDialog workflow={editableWorkflow}/>}
        </div>
    )
}
export default WorkflowGraphView
